/**
 * ListInfo.tsx - List information panel component
 * 
 * CHANGE HISTORY:
 * - 251130_1628 danielbeer: Created list info panel for ListManager
 * 
 * Displays metadata and details about the selected list.
 */

import React from 'react';
import type { ListData } from '../types';

export interface ListInfoProps {
  list: ListData;
  compact?: boolean;
}

/**
 * List information panel
 * 
 * @param props - Component props
 * @returns List info UI
 * 
 * @example
 * <ListInfo list={currentList} compact={true} />
 */
export const ListInfo: React.FC<ListInfoProps> = ({
  list,
  compact = false
}) => {
  return (
    <div style={{
      padding: compact ? '0.75rem' : '1rem',
      height: '100%',
      overflowY: 'auto'
    }}>
      <h4 style={{ 
        margin: '0 0 0.75rem 0', 
        fontSize: compact ? '0.8125rem' : '0.875rem', 
        fontWeight: 600,
        color: 'var(--text-primary)'
      }}>
        List Information
      </h4>
      
      {/* Compact Grid Layout */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: compact ? '90px 1fr' : '120px 1fr', 
        gap: compact ? '0.5rem' : '0.75rem',
        fontSize: compact ? '0.75rem' : '0.8125rem'
      }}>
        {/* Basic Info */}
        <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Name:</span>
        <span style={{ fontWeight: 500 }}>{list.name}</span>
        
        <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Description:</span>
        <span style={{ whiteSpace: 'pre-wrap' }}>{list.description || 'No description'}</span>
        
        <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Type:</span>
        <span>
          {list.type === 'dynamic' ? (
            <span title="Dynamic list - generated from query">🔄 Dynamic</span>
          ) : (
            <span title="Static list - manually curated">📋 Static</span>
          )}
        </span>
        
        <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>ID:</span>
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: compact ? '0.7rem' : '0.75rem',
          wordBreak: 'break-all'
        }}>
          {list.id}
        </span>
        
        <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Source:</span>
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: compact ? '0.7rem' : '0.75rem',
          wordBreak: 'break-all'
        }}>
          {list.source}
        </span>
        
        <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Items:</span>
        <span style={{ fontWeight: 500 }}>{list.children?.length || 0}</span>
        
        <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Updated:</span>
        <span>{list.lastUpdated}</span>
        
        <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Version:</span>
        <span>{list.version}</span>
        
        {/* Dynamic List Specific */}
        {list.type === 'dynamic' && (
          <>
            {list.refresh && (
              <>
                <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Refresh:</span>
                <span style={{ 
                  fontFamily: 'monospace', 
                  fontSize: compact ? '0.7rem' : '0.75rem',
                  whiteSpace: 'pre-wrap'
                }}>
                  {typeof list.refresh === 'object' ? JSON.stringify(list.refresh, null, 2) : String(list.refresh)}
                </span>
              </>
            )}
            {list.ttl && (
              <>
                <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>TTL:</span>
                <span>{list.ttl}s</span>
              </>
            )}
            {list.query && (
              <>
                <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Query:</span>
                <span style={{ 
                  fontFamily: 'monospace', 
                  fontSize: compact ? '0.7rem' : '0.75rem',
                  whiteSpace: 'pre-wrap'
                }}>
                  {JSON.stringify(list.query, null, 2)}
                </span>
              </>
            )}
          </>
        )}
        
        {/* Static List Specific */}
        {list.type === 'fixed' && (
          <>
            {list.sourceFile && (
              <>
                <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Source File:</span>
                <span style={{ 
                  fontFamily: 'monospace', 
                  fontSize: compact ? '0.7rem' : '0.75rem',
                  wordBreak: 'break-all'
                }}>
                  {list.sourceFile}
                </span>
              </>
            )}
            {list.sourceUrl && (
              <>
                <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Source URL:</span>
                <a 
                  href={list.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: 'var(--color-primary, #667eea)',
                    textDecoration: 'none',
                    fontSize: compact ? '0.7rem' : '0.75rem',
                    wordBreak: 'break-all'
                  }}
                >
                  {list.sourceUrl}
                </a>
              </>
            )}
          </>
        )}
        
        {/* Tags */}
        {list.tags && list.tags.length > 0 && (
          <>
            <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Tags:</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
              {list.tags.map(tag => (
                <span
                  key={tag}
                  style={{
                    padding: '0.125rem 0.5rem',
                    borderRadius: '9999px',
                    background: 'var(--surface-sunken)',
                    fontSize: '0.6875rem',
                    fontWeight: 500
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </>
        )}
        
        {/* Additional Metadata */}
        {list.metadata && Object.keys(list.metadata).length > 0 && (
          <>
            <span style={{ 
              gridColumn: '1 / -1',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginTop: '0.5rem',
              paddingTop: '0.5rem',
              borderTop: '1px solid var(--border-default)'
            }}>
              Metadata
            </span>
            {Object.entries(list.metadata).map(([key, value]) => (
              <React.Fragment key={key}>
                <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>
                  {key}:
                </span>
                <span style={{ 
                  fontFamily: typeof value === 'object' && value !== null ? 'monospace' : 'inherit',
                  fontSize: typeof value === 'object' && value !== null 
                    ? (compact ? '0.7rem' : '0.75rem')
                    : (compact ? '0.75rem' : '0.8125rem'),
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {(() => {
                    if (value === null) return 'null';
                    if (value === undefined) return 'undefined';
                    if (typeof value === 'object') {
                      try {
                        return JSON.stringify(value, null, 2);
                      } catch (e) {
                        return '[Object]';
                      }
                    }
                    return String(value);
                  })()}
                </span>
              </React.Fragment>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default ListInfo;
